import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { loginSalesperson } from "@/Calls/callSalespersonAuth";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function DealerLoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();
    const { setUser } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            console.log("email: ", email);
            console.log("password: ", password);

            const res = await loginSalesperson(email, password);

            if (res?.ok && res.user) {
                setUser({
                    id: res.user.id,
                    role: "SALESPERSON",
                    dealerId: String(res.user.dealer_id),
                    page: "/sales/dashboard",
                });
                navigate("/sales/dashboard", { replace: true });
            } else {
                setError(res?.message || "Login failed — please check your credentials.");
            }
        } catch (err) {
            console.error("Salesperson login error:", err);
            if (err instanceof Error) setError(err.message);
            else setError("Failed to connect to server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden p-4">
            <div className="z-10 w-full max-w-6xl">
                <div className="bg-secondary/50 overflow-hidden rounded-[40px] shadow-2xl">
                    <div className="grid min-h-[700px] lg:grid-cols-2">
                        {/* LEFT SIDE — Image */}
                        <div
                            className="relative m-4 rounded-3xl bg-cover bg-center p-12 text-white"
                            style={{
                                backgroundImage: "url('/images/assets/dealership_salesperson_futuristic_2.png')",
                            }}
                        />

                        {/* RIGHT SIDE — Login Form */}
                        <div className="flex flex-col justify-center p-12">
                            <div className="mx-auto w-full max-w-md">
                                <div className="mb-8 text-center">
                                    <h2 className="text-3xl font-light uppercase">Sales Person Login</h2>
                                    <p className="mt-2 text-sm text-stone-600">
                                        Sign in with your dealership credentials
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Email */}
                                    <div>
                                        <label htmlFor="email" className="mb-2 block text-sm font-medium uppercase">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <Input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="border-border bg-input block w-full rounded-lg border py-3 pr-3 pl-10 text-sm"
                                                placeholder="Enter your email address"
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <label htmlFor="password" className="mb-2 block text-sm font-medium uppercase">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <Input
                                                id="password"
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className="border-border bg-input block w-full rounded-lg border py-3 pr-12 pl-10 text-sm"
                                                placeholder="Enter your password"
                                            />
                                            <Button
                                                type="button"
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                className="absolute right-3 top-1/2 -translate-y-1/2"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-5 w-5 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-5 w-5 text-gray-400" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                                    {/* Submit */}
                                    <Button
                                        type="submit"
                                        className="relative flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-3 text-sm font-medium text-white transition-all duration-300 hover:opacity-90"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                <span className="ml-2">Signing in...</span>
                                            </>
                                        ) : (
                                            "Sign in to your account"
                                        )}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
